/* eslint-disable react/prop-types */
import { BETA_FEATURES, WAITLIST_GROUP, licenseStatusInfo } from '@/constants';
import * as dataActions from '@/data/data-actions';
import {
  betaFeatureConfigValue,
  isBetaFeatureEnabled,
  licenseEditButtonConfig,
} from '@/data/data-selectors';
import * as userSelectors from '@/user/user-selectors';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'rsuite';
import styled from 'styled-components';
import { AcceptCheckModal } from '../../../controls/components/accept-check-modal';
import { AdminUpdateConfigType } from '../../../types';
import useLicenseEditMode from '../../../utils/use-license-edit-mode';
import * as lc from '../license-content-styles';
import { LicenseModal } from '../license-modal';
import { LicenseStatusInfoText } from '../license-modules/license-status-info-text';
import { LicenseStatusDropdown } from '../license-status-dropdown';
import { LicenseEditorUI } from './license-editor';

const LicenseActions = styled.div`
  display: flex;
  flex-direction: row;
  gap: 18px;
`;

function LicenseEditorButton({ license, licenseIndex, disabled, loading }) {
  const [showLicenseEditor, setShowLicenseEditor] = useState(false);

  const dispatch = useDispatch();

  const { editModeConfig } = useLicenseEditMode();

  const allowEditingExpiryDate = useSelector(
    isBetaFeatureEnabled(BETA_FEATURES.licenseEditButtonExpirationDate)
  );

  const allowEditingName = useSelector(
    isBetaFeatureEnabled(BETA_FEATURES.licenseEditButtonRegistrantName)
  );

  const allowEditingFullName = useSelector(
    isBetaFeatureEnabled(BETA_FEATURES.licenseEditButtonRegistrantFullName)
  );

  const isEditOwnerBetaFlagEnabled = useSelector(
    isBetaFeatureEnabled(BETA_FEATURES.licenseEditButtonOwner)
  );

  const isRentalscapeEditorMode =
    editModeConfig.type === AdminUpdateConfigType.RentalscapeEditor;

  const allowEditingOwner =
    isEditOwnerBetaFlagEnabled && Array.isArray(license?.people?.owner);

  const config = useSelector(licenseEditButtonConfig);
  const licenseEditableFields = config?.editableFields;

  const onLicenseEdited = useCallback(
    ({ editedLicense, updatedMiscFields }) => {
      dispatch(
        dataActions.userUpdateLicense({
          license: editedLicense,
          originalLicense: license,
          index: licenseIndex,
          updatedMiscFields,
        })
      );
      setShowLicenseEditor(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [license, licenseIndex]
  );

  return (
    <>
      <lc.LicenseActionButton
        appearance="ghost"
        aria-label="Open Rentalscape permit editor"
        onClick={() => setShowLicenseEditor(true)}
        disabled={disabled}
        loading={loading}
      >
        {/* If mode isn't rs_on_page, there will be another edit button to edit the license. */}
        {isRentalscapeEditorMode ? 'Edit' : 'License settings'}
      </lc.LicenseActionButton>

      <LicenseEditorUI
        show={showLicenseEditor}
        license={license}
        onChange={onLicenseEdited}
        onHide={() => setShowLicenseEditor(false)}
        allowEditingExpiryDate={allowEditingExpiryDate}
        allowEditingName={allowEditingName}
        allowEditingFullName={allowEditingFullName}
        allowEditingOwner={allowEditingOwner}
        licenseEditableFields={licenseEditableFields}
        // Allow editing people only in rs_on_page mode
        showPeopleEditor={isRentalscapeEditorMode}
      />
    </>
  );
}

function AcceptAdminApplicationPaymentButton({ license, onCompletePayment }) {
  const applicationPayment = license?.applicationPayment;

  const [show, setShow] = useState(false);

  const acceptPayment = useCallback(() => {
    const uniqueCode = applicationPayment?.uniqueCode;
    if (!uniqueCode) return;
    setShow(true);
  }, [applicationPayment]);

  // Spreadsheet licenses should not have an application fee PaymentPeriod
  if (license?.misc?.is_spreadsheet_upload) return null;
  if (!applicationPayment) return <>Loading...</>;
  if (!applicationPayment.isAdminApplication) return null;

  return (
    <>
      <div>
        <div>Admin-initiated application</div>
        {applicationPayment?.isCompleted ? (
          'Payment Received'
        ) : (
          <Button appearance={'primary'} onClick={acceptPayment}>
            Accept application payment
          </Button>
        )}
      </div>
      <AcceptCheckModal
        show={show}
        setShow={setShow}
        uniqueCode={!show ? undefined : applicationPayment?.uniqueCode}
        onHideModal={onCompletePayment}
        allowOtherPaymentTypes
      />
    </>
  );
}

export function LicenseStatusSwitcher(props) {
  const { index, license, shouldShowRenewalsDropdown, refresh } = props;

  const [showLicense, setShowLicense] = useState(false);

  const userRights = useSelector(userSelectors.userRights);

  const { editModeConfig, onExternalEditButtonClick } = useLicenseEditMode();

  const editableLicenseTypes = useSelector(
    betaFeatureConfigValue(BETA_FEATURES.licenseEditButtonAllowedTypes)
  ) || ['str'];

  const adminUpdateFlowRestrictions = useSelector(
    betaFeatureConfigValue(BETA_FEATURES.adminUpdateFlowRestriction)
  );

  const isLicenseTypeEditable = editableLicenseTypes.includes(license?.type);

  const dispatch = useDispatch();

  const userUpdateLicenseStatus = useCallback(
    (status) => {
      dispatch(
        dataActions.userUpdateLicenseStatus({
          licenseDeckardid: license.licenseDeckardid,
          status,
          // Status prior to optimistic update to be used as fallback in case of update failure
          oldStatus: license.status,
          // Index of the current license being iterated through (used for optimistic update)
          index,
        })
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [license.licenseDeckardid, license.status, index]
  );

  const isWaitlistStatus =
    licenseStatusInfo[license?.status]?.group.includes(WAITLIST_GROUP);

  const isLicenseStatusUpdateInFlight =
    license.inFlight &&
    license.inFlightActionType === dataActions.UPDATE_LICENSE_STATUS;

  const isLicenseEditSavingInFlight =
    license.inFlight &&
    license.inFlightActionType === dataActions.UPDATE_LICENSE;

  const displayExternalEditLinkButton = useMemo(() => {
    if (!Array.isArray(adminUpdateFlowRestrictions))
      return editModeConfig.externalEditLinkMode;

    const isRestricted = adminUpdateFlowRestrictions.every(([path, value]) =>
      isEqual(get(props.license, path), value)
    );

    return !isRestricted && editModeConfig.externalEditLinkMode;
  }, [adminUpdateFlowRestrictions, props, editModeConfig.externalEditLinkMode]);

  useEffect(() => {
    function refreshOnAdminUpdateFlowCompletedMessage({ data }) {
      if (data?.adminUpdateFlowCompleted) {
        refresh();
      }
    }

    window.addEventListener(
      'message',
      refreshOnAdminUpdateFlowCompletedMessage
    );

    return () =>
      window.removeEventListener(
        'message',
        refreshOnAdminUpdateFlowCompletedMessage
      );
  }, [refresh]);

  const isAdminApplyFlowEnabled = useSelector(
    isBetaFeatureEnabled(BETA_FEATURES.adminApplyFlow)
  );

  const handleExternalEditButtonClick = () => {
    const { applicationNumber } = license.certificateInfo;
    onExternalEditButtonClick(applicationNumber);
  };

  return (
    <div>
      <LicenseModal
        index={index}
        license={license}
        showLicense={showLicense}
        setShowLicense={setShowLicense}
      />

      <div className="mt-8">
        <LicenseStatusInfoText
          type="certificateInfo"
          show={shouldShowRenewalsDropdown}
          status={license.status}
          certificateInfo={license.certificateInfo}
        />

        <LicenseActions>
          <LicenseStatusDropdown
            ariaLabel="Dropdown menu of permit statuses"
            disabled={Boolean(license.inFlight)}
            loading={isLicenseStatusUpdateInFlight}
            options={license.availableStatusChanges}
            selected={license.status}
            setSelected={userUpdateLicenseStatus}
          />

          <lc.LicenseActionButton
            appearance="ghost"
            onClick={() => setShowLicense(true)}
            disabled={
              !userRights.editLicense ||
              !license?.canSendEmail ||
              license.inFlight
            }
          >
            {`${license?.hasLicenseBeenSent ? 'Resend' : 'Send'} ${
              isWaitlistStatus ? 'invitation' : 'license'
            }`}
          </lc.LicenseActionButton>

          {displayExternalEditLinkButton && (
            <lc.LicenseActionButton
              appearance="ghost"
              aria-label="Initiate permit editing process"
              onClick={handleExternalEditButtonClick}
              disabled={!userRights.editLicense || license.inFlight}
            >
              Edit
            </lc.LicenseActionButton>
          )}

          <LicenseEditorButton
            license={license}
            licenseIndex={index}
            disabled={
              !userRights.editLicense ||
              license.inFlight ||
              !isLicenseTypeEditable
            }
            loading={isLicenseEditSavingInFlight}
          />
        </LicenseActions>

        <LicenseActions className={'mt-6'}>
          {isAdminApplyFlowEnabled && (
            <AcceptAdminApplicationPaymentButton
              license={license}
              onCompletePayment={refresh}
            />
          )}
        </LicenseActions>
      </div>
    </div>
  );
}
